// Sets the brand and theme on <html> before the first render. Imported for its side
// effect by every entry (main.tsx and the entry-*.tsx files) via src/brand/index.ts.

import { THEME_STORE, detectBrand, parseTheme, readStoredBrand, storeBrand, type BrandKey, type Theme } from "./brand";

export function currentBrand(): BrandKey {
  if (typeof document === "undefined") return "rb";
  return document.documentElement.dataset.brand === "cmc" ? "cmc" : "rb";
}

export function setTheme(theme: Theme): void {
  document.documentElement.dataset.cmcTheme = theme;
  try { window.localStorage.setItem(THEME_STORE, theme); } catch { /* the choice just is not remembered */ }
}

/** Bump when public/brand/cmc/skin.css changes, so a visitor's cached copy is not served. */
const SKIN_VERSION = "1";

/**
 * The brand's stylesheet is a plain file in /public, not part of the bundle: RaisedBeef's visitors never
 * download it. The page stays hidden until it arrives (or a second and a half passes) so the first paint
 * is already the right brand, not a flash of the old colours.
 */
function loadSkin(root: HTMLElement): void {
  root.dataset.brandLoading = "1";
  const done = () => { delete root.dataset.brandLoading; };
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `/brand/cmc/skin.css?v=${SKIN_VERSION}`;
  link.onload = done;
  link.onerror = done;
  document.head.appendChild(link);
  // Inter, from Google Fonts, per the typography spec (the site already loads fonts from there).
  const inter = document.createElement("link");
  inter.rel = "stylesheet";
  inter.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;450;500;550;600;650;700&display=swap";
  document.head.appendChild(inter);
  window.setTimeout(done, 1500);
}

export function initBrand(): BrandKey {
  if (typeof window === "undefined") return "rb";
  const brand = detectBrand(window.location.hostname, window.location.search, readStoredBrand());
  storeBrand(brand);
  const root = document.documentElement;
  if (brand === "cmc") {
    root.dataset.brand = "cmc";
    let theme: Theme = "light";
    try { theme = parseTheme(window.localStorage.getItem(THEME_STORE)); } catch { /* light */ }
    root.dataset.cmcTheme = theme;
    loadSkin(root);
    // The browser tab and home-screen icons are the brand's, not RaisedBeef's.
    const setIcon = (rel: string, href: string, sizes?: string) => {
      document.querySelectorAll(`link[rel="${rel}"]`).forEach((l) => l.remove());
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      if (sizes) link.setAttribute("sizes", sizes);
      link.type = "image/png";
      document.head.appendChild(link);
    };
    setIcon("icon", "/brand/cmc/favicon-32.png", "32x32");
    setIcon("apple-touch-icon", "/brand/cmc/apple-touch-icon.png", "180x180");
    // The demo subdomain stays out of search results; cattlemarketcentral.com is the site itself
    // (owner, 2026-11-03: raisedbeef.com redirects to it), so it is indexed.
    if (window.location.hostname === "cattlemarketcentral.raisedbeef.com") {
      const robots = document.createElement("meta");
      robots.name = "robots";
      robots.content = "noindex, nofollow";
      document.head.appendChild(robots);
    }
    // loaded only for the brand: RaisedBeef's visitors never download the rewrite
    void import("./rebrandText").then((m) => m.startRebrand());
  } else {
    delete root.dataset.brand;
    delete root.dataset.cmcTheme;
  }
  return brand;
}
